<template>
  <q-modal
    :show="showModal || showNoFiltersModal"
    :title="
      editModal ? $t('conversations.filters.form.update_heading') : $t('conversations.filters.form.create_heading')
    "
    :hanging-content="filterUpdatedInfo"
    scrollable
    wide
    @close="$emit('close')"
  >
    <template v-if="promptTesting" #header>
      <div class="flex flex-1 items-center gap-2">
        <p class="grow text-lg font-medium">{{ $t('settings.ai.conversations_filter_modal.title') }}</p>
        <div class="flex items-center gap-2">
          <span class="text-gray-60 text-sm font-medium">
            {{ $t('settings.ai.conversations_filter_modal.workspace_label') }}
          </span>
          <prompt-testing-workspace-selection @switch-workspace="switchWorkspace" />
        </div>
      </div>
    </template>
    <div v-if="loadingModal" class="text-center text-gray-50">
      <p>{{ $t('conversations.filters.form.loading_options_message') }}</p>
      <ui-spinner class="modal-loader" />
    </div>
    <template v-else>
      <ui-alert v-if="noFilters && !promptTesting" warning class="mb-6">
        <i18n-t keypath="conversations.filters.form.no_filters_message">
          <template #recommendedFilter>
            "<strong>{{ $t('conversations.filters.form.recommended_filter') }}</strong
            >"
          </template>
        </i18n-t>
      </ui-alert>

      <ui-alert v-if="promptTesting && filterTicketCount === 0 && !loadingTickets" error small class="mb-6">
        {{ $t('settings.ai.conversations_filter_modal.error') }}
      </ui-alert>

      <form class="settings-form !mt-5" @submit.prevent="addFilter">
        <div v-if="!promptTesting" class="flex items-start gap-6">
          <ui-form-field
            ref="nameFieldRef"
            class="w-80"
            :label="$t('conversations.filters.form.name_field_label')"
            :error="!newFilterName && saveClicked ? $t('filter.name_empty') : undefined"
          >
            <ui-input v-model.trim="newFilterName" name="name" required maxlength="255" />
          </ui-form-field>

          <q-toggle
            v-if="!hasWorkspaceRole('AGENT')"
            v-model="newFilterPublicity"
            :label="$t('conversations.filters.form.visibility_toggle_label')"
            :options="[
              { label: $t('conversations.filters.form.public_option_label'), value: true, icon: UnlockIcon },
              { label: $t('conversations.filters.form.private_option_label'), value: false, icon: LockIcon },
            ]"
          />
        </div>

        <ui-alert v-if="warningMessage" warning class="my-6">
          {{ warningMessage }}
        </ui-alert>

        <filter-options
          v-if="fetchedFilterOptions"
          id="add-new-filters"
          v-model="newFilterOptions"
          class="mb-6 mt-14"
          :edit-modal="editModal"
          is-modal
          :options="fetchedFilterOptions.defaultFilterOptions"
          :autoqa-fields="fetchedFilterOptions.autoQaFilterOptions"
          :spotlight-fields="fetchedFilterOptions.spotlightFilterOptions"
          :custom-category-fields="fetchedFilterOptions.customCategoryFilterOptions"
          :intelligent-filter-fields="fetchedFilterOptions.intelligentFilterOptions"
          :hide-all-conversation-filters="promptTesting"
          :modal-type="promptTesting && FilterModalType.PromptTesting"
          @disable-selecting-tickets="disableSelectingTickets = !$event"
        />
      </form>
    </template>

    <template v-if="!loadingModal" #actions>
      <div class="text-gray-60 mr-auto flex flex-col text-sm">
        <span class="flex flex-row gap-1">
          <i18n-t
            tag="div"
            keypath="conversations.filters.form.ticket_count_label_with_appendix"
            class="flex"
            :plural="filterTicketCount"
          >
            <template #count>
              <ui-spinner v-if="loadingTickets" class="tickets-loader small" />
              <span v-else class="text-gray-80 ml-1 font-medium">
                <template v-if="showTicketCountError">{{ $t('conversations.filters.form.ticket_count_na') }}</template>
                <template v-else-if="filterTicketCount > 999 && !session.features.showExactTicketCount">
                  {{ $n(999, { signDisplay: 'always' }) }}
                </template>
                <template v-else-if="filterTicketCount === 0 && promptTesting && !loadingTickets">
                  <span class="text-red-50">{{ filterTicketCount }}</span>
                </template>
                <template v-else>{{ filterTicketCount }}</template>
              </span>
            </template>
            <template #appendix>
              <template v-if="!loadingTickets && !hasTimeframeFilter && !promptTesting">
                <span class="ml-1">{{ $t('conversations.filters.form.over_last_30_days') }}</span>
                <span
                  v-tippy="{ content: $t('conversations.filters.form.timeframe_tooltip'), delay }"
                  class="bg-gray-10 ml-1 flex h-5 w-5 self-center rounded-xl p-1"
                >
                  <info-icon class="icon-m self-center" />
                </span>
              </template>
              <template v-if="promptTesting && filterTicketCount >= 100">
                <span class="ml-1">{{
                  $t('settings.ai.conversations_filter_modal.count_description', { count: 100 })
                }}</span>
                <span
                  v-tippy="{
                    content: $t('settings.ai.conversations_filter_modal.count_info_tooltip', { count: 100 }),
                    delay,
                  }"
                  class="bg-gray-10 ml-1 flex h-5 w-5 self-center rounded-xl p-1"
                >
                  <info-icon class="icon-m self-center" />
                </span>
              </template>
            </template>
          </i18n-t>
        </span>
        <a
          :href="$t('support_links.find_conversations_to_review')"
          target="_blank"
          rel="noopener noreferrer"
          class="content-link"
        >
          {{ $t('conversations.filters.form.read_more') }}
        </a>
      </div>

      <ui-button v-if="canConvertToAssignment" class="mr-3" secondary @click="convertToAssignment">
        {{ $t('conversations.filters.form.convert') }}
      </ui-button>
      <ui-button v-if="!noFilters || promptTesting" secondary class="mr-3" @click="cancelModal">
        {{ $t('universal.cancel') }}
      </ui-button>
      <ui-button type="submit" :disabled="disableSubmit" @click="addFilter">
        {{
          editModal
            ? $t('conversations.filters.form.update_submit')
            : promptTesting
              ? $t('settings.ai.conversations_filter_modal.submit_button')
              : $t('conversations.filters.form.create_submit')
        }}
      </ui-button>
    </template>
  </q-modal>
</template>

<script lang="ts" setup>
import { useRouter } from 'vue-router'
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue'
import { InfoIcon, LockIcon, UnlockIcon } from '@klausapp/ui-kit/icons'
import { UiButton, UiFormField, UiInput, UiSpinner, UiAlert } from '@klausapp/ui-kit'
import { v4 as uuid } from 'uuid'

import { formatDateIntl } from '@/utils/date'
import QModal from '@/components/QModal.vue'
import { toast } from '@/components/toast'
import analytics, { track } from '@/utils/analytics'
import { session } from '@/composables/useSession'
import i18n from '@/i18n'
import { hasWorkspaceRole } from '@/utils/roleUtils'
import { bus } from '@/utils/bus'
import { delay } from '@/components/Tippy'
import useAutoQaDateWarning from '@/composables/useAutoQaDateWarning'
import useReviewSidebar from '@/composables/useReviewSidebar/useReviewSidebar'
import QToggle from '@/components/QToggle.vue'
import useAiConversations from '@/modules/settings/composables/useAiConversations'
import PromptTestingWorkspaceSelection from '@/modules/settings/components/ai/PromptTestingWorkspaceSelection.vue'
import usePromptTestingWorkspace from '@/modules/settings/composables/usePromptTestingWorkspace'
import { FilterModalType } from '@/components/filters/types'
import { workspacesLoading } from '@/composables/useWorkspaceList'
import useFilterValues from '@/modules/conversations/composables/useFilterValues'

import {
  activeFilterId,
  privateFilters,
  publicFilters,
  setActiveFilterId,
} from '../../composables/useConversationFilters'

import {
  constructFilterOptions,
  constructDynamicFilterOptions,
  dateConditions,
  deconstructFilterOptions,
  generateFilterLabels,
  getDayjsRangeFromOption,
  type DeconstructedOption,
} from '../../utils/filters'
import {
  type BaseFilter,
  createFilter,
  editFilter,
  getFilterOptions as getFilterOptionsApi,
  getFilterDetails,
  memGetFilterTicketCount,
} from '../../api/filters'
import FilterOptions from './FilterOptions.vue'

const props = defineProps<{
  showModal: boolean
  showNoFiltersModal: boolean
  public: boolean
  promptNewFilter?: boolean
  editableFilter?: BaseFilter
  promptTesting?: boolean
  canConvertToAssignment?: boolean
  initialFilterOptions?: DeconstructedOption[]
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'update'): void
  (e: 'stop-ticket-list-loading'): void
  (e: 'select-tickets-for-testing', ticketCount: number): void
}>()

const { displayAsEmoji } = useReviewSidebar()
const { promptTestingConversationsFilterOptions } = useAiConversations()
const { activeWorkspace, revertWorkspace, submitWorkspace } = usePromptTestingWorkspace()
const { fetchedFilterOptions } = useFilterValues()

const router = useRouter()
const editModal = ref(false)
const editFilterId = ref(null)
const newFilterPublicity = ref(false)
const newFilterOptions = ref<DeconstructedOption[]>([])
const newFilterName = ref('')
const saveClicked = ref(false)
const filterTicketCount = ref<number>(0)
const showTicketCountError = ref(false)
const loadingModal = ref(false)
const loadingTickets = ref(false)
const loadingFilterOptions = ref(false)
const filterUpdatedInfo = ref('')
const nameFieldRef = ref(null)
const disableSelectingTickets = ref(false)

const noFilters = computed(() => !publicFilters.value.length && !privateFilters.value.length)
const hasTimeframeFilter = computed(() => {
  return newFilterOptions.value.some(({ option }) => dateConditions.includes(option.name))
})

/* conversation filters should always have time constraint so requests wouldn't take too long */
const getEnhancedFilterOptions = (options: DeconstructedOption[]) => {
  const enhancedOptions = constructFilterOptions(
    options.filter(
      (o) => !o.option.isAutoQa && !o.option.isSpotlight && !o.option.isCustomCategory && !o.option.isIntelligent,
    ),
  )

  if (!hasTimeframeFilter.value) {
    enhancedOptions.push({ type: 'DEFAULT', option: 'TICKET_CREATED_DATE', condition: 'DATE_30_DAYS' })
  }

  return enhancedOptions
}

watch(
  () => props.showModal,
  (showModal) => {
    if (showModal) showFilterModal()
    else saveClicked.value = false
  },
)

const selectedAutoQaFilterNames = computed(() =>
  newFilterOptions.value.filter((o) => o.option.isAutoQa).map(({ option }) => option.name?.toLowerCase()),
)

const dateRange = computed(() => {
  const createdDateOption = newFilterOptions.value.find(({ option }) => option.name === 'TICKET_CREATED_DATE')
  if (!createdDateOption) return null

  return getDayjsRangeFromOption(createdDateOption)
})

const { warningMessage } = useAutoQaDateWarning({
  workspaceId: session.workspace.id,
  dateRange,
  usedAutoQaCategories: selectedAutoQaFilterNames,
})

const disableSubmit = computed(() => {
  if (props.promptTesting)
    return (
      loadingTickets.value ||
      loadingFilterOptions.value ||
      disableSelectingTickets.value ||
      filterTicketCount.value === 0
    )
  return false
})

const cancelModal = () => {
  emit('close')
  if (props.promptTesting) {
    newFilterOptions.value = []
    revertWorkspace()
  }
}

watch(
  [newFilterOptions, workspacesLoading],
  async ([options]) => {
    if (!options.length) return
    loadingTickets.value = true

    try {
      showTicketCountError.value = false
      const { data } = await memGetFilterTicketCount(
        {
          isPublic: newFilterPublicity.value,
          name: newFilterName.value || 'temporary-name',
          options: getEnhancedFilterOptions(options),
          autoQaOptions: constructFilterOptions(options.filter((o) => o.option.isAutoQa)),
          spotlightOptions: constructFilterOptions(options.filter((o) => o.option.isSpotlight)),
          customCategoryOptions: constructFilterOptions(options.filter((o) => o.option.isCustomCategory)),
          intelligentFilterOptions: constructFilterOptions(options.filter((o) => o.option.isIntelligent)),
        },
        props.promptTesting && activeWorkspace.value?.id
          ? `payments/${session.account.id}/teams/${activeWorkspace.value.id}`
          : undefined,
      )
      filterTicketCount.value = data
    } catch {
      filterTicketCount.value = 0
      showTicketCountError.value = true
    } finally {
      loadingTickets.value = false
    }
  },
  { deep: true },
)

onMounted(() => {
  if (props.promptNewFilter) showFilterModal()
})

onBeforeUnmount(() => {
  editModal.value = false
})

const addFilter = async () => {
  if (props.promptTesting) {
    promptTestingConversationsFilterOptions.value = newFilterOptions.value
    selectTicketsForPromptTesting()
    submitWorkspace()
    return
  }

  saveClicked.value = true

  if (!newFilterName.value?.length) {
    toast({ status: 'error', message: i18n.t('filter.empty_name_toast') })
    nameFieldRef.value?.$el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    return
  }

  if (!hasTimeframeFilter.value) {
    toast({ status: 'error', message: i18n.t('filter.date_condition_toast') })
    document.querySelector('[data-testid=date-condition-group]')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    return
  }

  const data = {
    isPublic: newFilterPublicity.value,
    name: newFilterName.value,
    options: constructFilterOptions(
      newFilterOptions.value.filter(
        (o) => !o.option.isAutoQa && !o.option.isSpotlight && !o.option.isCustomCategory && !o.option.isIntelligent,
      ),
    ),
    autoQaOptions: constructFilterOptions(newFilterOptions.value.filter((o) => o.option.isAutoQa)),
    spotlightOptions: constructFilterOptions(newFilterOptions.value.filter((o) => o.option.isSpotlight)),
    customCategoryOptions: constructFilterOptions(newFilterOptions.value.filter((o) => o.option.isCustomCategory)),
    intelligentFilterOptions: constructFilterOptions(newFilterOptions.value.filter((o) => o.option.isIntelligent)),
  }

  let filterId: number | undefined = undefined

  if (editModal.value) {
    if (editFilterId.value) {
      filterId = await editFilter({ id: editFilterId.value, data }).then(({ data }) => data)
      emit('update')
    }
  } else {
    filterId = await createFilter(data).then(({ data }) => data)
  }

  if (filterId) setFilter(filterId)
  analytics.filterChanged({ editModal: editModal.value, isPublic: newFilterPublicity.value, name: newFilterName.value })

  const hasBotFilter = newFilterOptions.value.some((o) => ['BOTS', 'AI_AGENTS'].includes(o.option.name))
  const hasBotReplyFilter = newFilterOptions.value.some((o) =>
    ['BOT_REPLY_COUNT', 'AI_AGENT_REPLY_COUNT'].includes(o.option.name),
  )
  if (hasBotFilter) analytics.botFilterExists()
  if (hasBotReplyFilter) analytics.botReplyFilterExists()

  data.autoQaOptions.forEach((o) => track(`Conversation filter saved with condition: ${o.option.toLowerCase()}`))

  toast({
    status: 'success',
    message: editModal.value
      ? i18n.t('conversations.filters.update_success_toast')
      : i18n.t('conversations.filters.add_success_toast'),
  })
  editModal.value = false
  emit('close')
  newFilterOptions.value = []
  newFilterName.value = ''
  newFilterPublicity.value = false
}

const selectTicketsForPromptTesting = () => {
  emit('select-tickets-for-testing', filterTicketCount.value)
  newFilterOptions.value = []
}

const showFilterModal = async (_?: any, localFilterOptions?: any) => {
  editModal.value = false
  loadingModal.value = true

  try {
    if (!fetchedFilterOptions.value) await getFilterOptions()
  } catch {
    toast({ status: 'error', message: i18n.t('conversations.filters.form.loading_error') })
    loadingModal.value = false
    return
  }

  if (props.editableFilter) {
    editModal.value = true
    // Load filter details
    editFilterId.value = props.editableFilter.filterId
    newFilterName.value = props.editableFilter.name
    newFilterPublicity.value = props.editableFilter.isPublic
    filterUpdatedInfo.value = getFilterUpdatedInfo(props.editableFilter)

    const filterDetails = await getFilterDetails(props.editableFilter.filterId, false).then(({ data }) => data)

    newFilterOptions.value = deconstructFilterOptions({
      options: filterDetails.options || [],
      autoQaOptions: filterDetails.autoQaOptions || [],
      spotlightOptions: filterDetails.spotlightOptions || [],
      customCategoryOptions: filterDetails.customCategoryOptions || [],
      intelligentFilterOptions: filterDetails.intelligentFilterOptions || [],
      defaultOptions: fetchedFilterOptions.value?.defaultFilterOptions,
      defaultAutoQaOptions: fetchedFilterOptions.value?.autoQaFilterOptions,
      defaultSpotlightOptions: fetchedFilterOptions.value?.spotlightFilterOptions,
      defaultCustomCategoryOptions: fetchedFilterOptions.value?.customCategoryFilterOptions,
      defaultIntelligentFilterOptions: fetchedFilterOptions.value?.intelligentFilterOptions,
      displayAsEmoji: displayAsEmoji.value,
    })
  } else {
    editFilterId.value = null
    newFilterName.value = ''
    if (props.initialFilterOptions) {
      newFilterOptions.value = props.initialFilterOptions
    } else {
      newFilterOptions.value = localFilterOptions || [
        {
          id: uuid(),
          option: { name: 'TICKET_CREATED_DATE', label: 'filter.option.label.ticket_created_date' },
          condition: { name: 'DATE_30_DAYS', label: 'filter.condition.label.date_30_days', valueRequired: false },
          values: [],
        },
      ]
    }

    newFilterPublicity.value = props.public
    filterUpdatedInfo.value = undefined
  }

  loadingModal.value = false
  filterTicketCount.value = 0
}

async function switchWorkspace(workspaceId: number) {
  loadingModal.value = true
  try {
    await getFilterOptions(`payments/${session.account.id}/teams/${workspaceId}`)
    const availableOptionNames = [...Object.values(fetchedFilterOptions.value || {}).flat()].map((o) => o.filterOption)
    newFilterOptions.value = newFilterOptions.value.filter((o) => availableOptionNames.includes(o.option.name))
  } catch {
    toast({ status: 'error', message: i18n.t('conversations.filters.form.loading_error') })
    return
  } finally {
    loadingModal.value = false
  }
}

const displayNoFiltersPrompt = async () => {
  const assignedToAgentFilter = constructDynamicFilterOptions({
    forFilter: true,
    options: ['ASSIGNEE'],
    conditions: ['LONG_LIST_EQUALS'],
    values: [1],
  })
  loadingModal.value = true

  if (!fetchedFilterOptions.value) {
    loadingFilterOptions.value = true
    await getFilterOptionsApi()
    loadingFilterOptions.value = false
  }
  const labelledOptions = fetchedFilterOptions.value?.defaultFilterOptions.length
    ? generateFilterLabels(assignedToAgentFilter, fetchedFilterOptions.value?.defaultFilterOptions)
    : []
  await showFilterModal(null, labelledOptions)
  newFilterName.value = 'My tickets'
  bus.$emit('ticket-hide-loader')
}

const getFilterUpdatedInfo = (filter) => {
  const { updated, created, updateByName, ownerName } = filter
  const agentName = updateByName || ownerName || i18n.t('universal.former_member', 2)
  return updated
    ? i18n.t('conversations.filters.form.filter_updated', { time: formatDateIntl(updated || created), agentName })
    : i18n.t('conversations.filters.form.filter_created', { time: formatDateIntl(updated || created), agentName })
}

const getFilterOptions = async (reqPrefix?: string) => {
  fetchedFilterOptions.value = await getFilterOptionsApi(reqPrefix)
    .then(({ data }) => data)
    .catch((err) => {
      throw err
    })
}

const setFilter = (id: number | string) => {
  const isNumericFilter = !isNaN(id as number)
  setActiveFilterId(id)
  if (!activeFilterId.value || (isNumericFilter && activeFilterId.value === id)) emit('update')
}

watch(
  () => props.showNoFiltersModal,
  (state) => {
    if (state) {
      displayNoFiltersPrompt()
      emit('stop-ticket-list-loading')
    }
  },
  { immediate: true },
)

const convertToAssignment = () => {
  editModal.value = false
  emit('close')
  router.push({
    name: 'workspace.assignments.agent-qa.new',
    params: { id: session.workspace.id },
    query: { name: newFilterName.value || '', conditions: encodeURIComponent(JSON.stringify(newFilterOptions.value)) },
  })
}
</script>

<style lang="scss" scoped>
.filter-bar {
  position: relative;
  flex-direction: column;
  flex-shrink: 0;
  background-color: var(--gray-80);
  color: var(--white);
  overflow-x: hidden;

  &__button {
    padding: 8px 12px;
    color: var(--gray-40);

    &:hover {
      color: var(--white);
    }
  }

  &__header {
    position: sticky;
    top: 0;
    padding: 12px 20px;
    z-index: 4;
    background-color: var(--gray-80);
  }

  &__title {
    display: flex;
    align-items: center;
    padding-top: 10px;
    height: 50px;
  }
  &__title-text {
    margin-right: auto;
    font-size: theme('fontSize.lg');
    font-weight: theme('fontWeight.medium');
  }
}

.tickets-loader {
  position: relative;
  flex: 1;
  margin-left: 16px;
}

.modal-loader {
  position: relative;
  min-height: 300px;
}
</style>
